/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { LogViewer } from '@backstage/core-components';
import { Icon } from '@iconify/react';
import { Button, Drawer, Grid, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { tss } from 'tss-react/mui';

const useDrawerStyles = tss.create(({ theme }) => ({
  logs: {
    background: theme.palette.background.default,
  },
  paper: {
    padding: theme.spacing(2.5),
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
    [theme.breakpoints.up('sm')]: {
      width: '75%',
    },
    width: '100%',
  },
  root: {
    height: '100%',
    overflow: 'hidden',
  },
}));

const TechDocsBuildLogsDrawerContent = ({
  buildLog,
  onClose,
}: {
  buildLog: string[];
  onClose: () => void;
}) => {
  const { classes } = useDrawerStyles();
  const logText =
    buildLog.length === 0 ? 'Waiting for logs...' : buildLog.join('\n');
  return (
    <Grid
      className={classes.root}
      container
      direction="column"
      spacing={0}
      wrap="nowrap"
    >
      <Grid
        alignItems="center"
        container
        item
        justifyContent="space-between"
        spacing={0}
        wrap="nowrap"
      >
        <Typography variant="h5">Build Details</Typography>
        <IconButton
          color="inherit"
          key="dismiss"
          onClick={onClose}
          title="Close the drawer"
        >
          <Icon icon="material-symbols:close" />
        </IconButton>
      </Grid>
      <Grid item xs>
        <LogViewer classes={{ root: classes.logs }} text={logText} />
      </Grid>
    </Grid>
  );
};

export const TechDocsBuildLogs = ({ buildLog }: { buildLog: string[] }) => {
  const { classes } = useDrawerStyles();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button color="inherit" onClick={() => setOpen(true)}>
        Show Build Logs
      </Button>
      <Drawer
        anchor="right"
        classes={{ paper: classes.paper }}
        onClose={() => setOpen(false)}
        open={open}
      >
        <TechDocsBuildLogsDrawerContent
          buildLog={buildLog}
          onClose={() => setOpen(false)}
        />
      </Drawer>
    </>
  );
};
